import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { useGetQuestions } from "@src/appV2/redesign/Workplace/Review/api/useGetQuestions";
import { usePostRating } from "@src/appV2/redesign/Workplace/Review/api/usePostRating";
import { usePostReview } from "@src/appV2/redesign/Workplace/Review/api/usePostReview";
import { useCallback, useMemo } from "react";

import { CbhFeatureFlag, useCbhFlag } from "../FeatureFlags";
import { RecordVideoPage } from "../redesign/Social/RecordVideoPage";
import { ReviewQuestionType } from "../redesign/Workplace/Review/types";
import { PostShiftReviewFlowContent } from "./PostShiftReviewFlowContent";
import {
  type PostShiftReviewFlowProps,
  type PostShiftReviewFormValues,
  QuestionTag,
} from "./types";

export function PostShiftReviewFlow(props: PostShiftReviewFlowProps) {
  const { modalState, facility, onComplete, shiftId } = props;

  const isVideoPostingEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_VIDEO_POSTING, {
    defaultValue: false,
  });

  const { showErrorToast } = useToast();

  const {
    data: questionsData,
    isLoading,
    error,
  } = useGetQuestions({
    workplaceType: facility.type,
    workplaceId: facility.id,
  });

  const isError = Boolean(error);

  const submitRatingMutation = usePostRating(facility.id, {
    onSuccess: (response) => {
      logEvent(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_RATING_SUBMITTED, {
        workplaceId: facility.id,
        rating: response.data.attributes.rating,
        shiftId,
      });
    },
    onError: () => {
      showErrorToast("Failed to submit rating. Please try again.");
    },
  });

  const submitReviewMutation = usePostReview(facility.id, {
    onSuccess: () => {
      logEvent(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_REVIEW_SUBMITTED, {
        workplaceId: facility.id,
        shiftId,
      });
    },
    onError: () => {
      showErrorToast("Failed to submit review. Please try again.");
    },
  });

  const questionMappings = useMemo(() => {
    const supportedTags = [
      QuestionTag.SOFTWARE,
      QuestionTag.SUPPLIES,
      QuestionTag.PATIENTS_PER_NURSE,
      QuestionTag.PARKING,
    ];

    const mappings: {
      commentQuestionId: string | undefined;
      tagToQuestionId: Partial<Record<QuestionTag, string>>;
      questionOptions: Record<string, Array<{ id: string; displayText: string }>>;
    } = {
      commentQuestionId: undefined,
      tagToQuestionId: {},
      questionOptions: {},
    };

    if (!questionsData?.data) {
      return mappings;
    }

    const commentQuestion = questionsData.data.find(
      (question) => question.attributes.type === ReviewQuestionType.COMMENT
    );
    if (commentQuestion) {
      mappings.commentQuestionId = commentQuestion.id;
    }

    for (const tag of supportedTags) {
      const matchingQuestion = questionsData.data.find((question) =>
        question.attributes.tags?.some((tagInfo) => tagInfo.tag === tag)
      );

      if (matchingQuestion && !mappings.tagToQuestionId[tag]) {
        mappings.tagToQuestionId[tag] = matchingQuestion.id;

        if (matchingQuestion.attributes.options?.length > 0) {
          mappings.questionOptions[matchingQuestion.id] = matchingQuestion.attributes.options.map(
            (opt) => ({ id: opt.id, displayText: opt.displayText })
          );
        }
      }
    }

    return mappings;
  }, [questionsData]);

  const handleSubmitRating = useCallback(
    async (rating: number) => {
      await submitRatingMutation.mutateAsync({ rating });
    },
    [submitRatingMutation]
  );

  const createQuestionAnswerWithOptions = useCallback(
    (
      answer: string,
      otherText: string | undefined,
      questionId: string,
      options: Array<{ id: string; displayText: string }>
    ) => {
      const selectedOption = options.find((option) => option.displayText === answer);
      const answerData: { questionId: string; text: string; optionId?: string } = {
        questionId,
        text: answer === "Other" ? otherText ?? "" : answer,
      };

      if (selectedOption?.id) {
        answerData.optionId = selectedOption.id;
      }

      return answerData;
    },
    []
  );

  const addQuestionAnswers = useCallback(
    (
      formData: PostShiftReviewFormValues,
      answers: Array<{
        questionId: string;
        text: string;
        optionId?: string;
      }>
    ) => {
      if (formData.softwareAnswer && questionMappings.tagToQuestionId[QuestionTag.SOFTWARE]) {
        const questionId = questionMappings.tagToQuestionId[QuestionTag.SOFTWARE];
        const options = questionMappings.questionOptions[questionId] || [];
        answers.push(
          createQuestionAnswerWithOptions(
            formData.softwareAnswer,
            formData.softwareOtherText,
            questionId,
            options
          )
        );
      }

      if (formData.suppliesAnswer && questionMappings.tagToQuestionId[QuestionTag.SUPPLIES]) {
        const questionId = questionMappings.tagToQuestionId[QuestionTag.SUPPLIES];
        const options = questionMappings.questionOptions[questionId] || [];
        answers.push(
          createQuestionAnswerWithOptions(formData.suppliesAnswer, undefined, questionId, options)
        );
      }

      if (
        formData.patientCountAnswer &&
        questionMappings.tagToQuestionId[QuestionTag.PATIENTS_PER_NURSE]
      ) {
        const questionId = questionMappings.tagToQuestionId[QuestionTag.PATIENTS_PER_NURSE];
        const options = questionMappings.questionOptions[questionId] || [];
        answers.push(
          createQuestionAnswerWithOptions(
            formData.patientCountAnswer,
            undefined,
            questionId,
            options
          )
        );
      }

      if (
        formData.parkingAnswer !== undefined &&
        questionMappings.tagToQuestionId[QuestionTag.PARKING]
      ) {
        const questionId = questionMappings.tagToQuestionId[QuestionTag.PARKING];
        answers.push({
          questionId,
          text: formData.parkingAnswer,
        });
      }
    },
    [questionMappings, createQuestionAnswerWithOptions]
  );

  const handleSubmitReview = useCallback(
    async (formData: PostShiftReviewFormValues) => {
      const answers: Array<{ questionId: string; text: string; optionId?: string }> = [];

      if (formData.reviewText.trim() && questionMappings.commentQuestionId) {
        answers.push({
          questionId: questionMappings.commentQuestionId,
          text: formData.reviewText,
        });
      }

      addQuestionAnswers(formData, answers);

      await submitReviewMutation.mutateAsync({
        anonymous: formData.isAnonymous ?? false,
        answers,
      });
    },
    [addQuestionAnswers, questionMappings, submitReviewMutation]
  );

  if (isVideoPostingEnabled) {
    return (
      <RecordVideoPage
        modalState={modalState}
        workplaceId={facility.id}
        workplaceName={facility.name}
      />
    );
  }

  return (
    <PostShiftReviewFlowContent
      modalState={modalState}
      facility={facility}
      shiftId={shiftId}
      questionMappings={questionMappings}
      isLoading={isLoading}
      isError={isError}
      onComplete={onComplete}
      onSubmitRating={handleSubmitRating}
      onSubmitReview={handleSubmitReview}
    />
  );
}
